import { useState, useMemo, useEffect } from 'react';
import { ComposableMap, Geographies, Geography } from 'react-simple-maps';
import useDealStore from '../store/dealStore';
import LiveMarketPulse from '../components/LiveMarketPulse';
import useMarketData from '../hooks/useMarketData';
import { fetchPublicStateScores } from '../services/marketDataService';
import '../styles/module2.css';

const GEO_URL = '/states-10m.json';

/* State name → 2-letter code */
const NAME_TO_CODE = {
  'Alabama': 'AL', 'Alaska': 'AK', 'Arizona': 'AZ', 'Arkansas': 'AR', 'California': 'CA',
  'Colorado': 'CO', 'Connecticut': 'CT', 'Delaware': 'DE', 'Florida': 'FL', 'Georgia': 'GA',
  'Hawaii': 'HI', 'Idaho': 'ID', 'Illinois': 'IL', 'Indiana': 'IN', 'Iowa': 'IA',
  'Kansas': 'KS', 'Kentucky': 'KY', 'Louisiana': 'LA', 'Maine': 'ME', 'Maryland': 'MD',
  'Massachusetts': 'MA', 'Michigan': 'MI', 'Minnesota': 'MN', 'Mississippi': 'MS',
  'Missouri': 'MO', 'Montana': 'MT', 'Nebraska': 'NE', 'Nevada': 'NV', 'New Hampshire': 'NH',
  'New Jersey': 'NJ', 'New Mexico': 'NM', 'New York': 'NY', 'North Carolina': 'NC',
  'North Dakota': 'ND', 'Ohio': 'OH', 'Oklahoma': 'OK', 'Oregon': 'OR', 'Pennsylvania': 'PA',
  'Rhode Island': 'RI', 'South Carolina': 'SC', 'South Dakota': 'SD', 'Tennessee': 'TN',
  'Texas': 'TX', 'Utah': 'UT', 'Vermont': 'VT', 'Virginia': 'VA', 'Washington': 'WA',
  'West Virginia': 'WV', 'Wisconsin': 'WI', 'Wyoming': 'WY',
};

const normalizeStateCode = (state) => {
  if (!state) return 'TX';
  const clean = String(state).trim();
  const upper = clean.toUpperCase();
  if (STATE_DATA[upper]) return upper;
  return NAME_TO_CODE[clean] || 'TX';
};

/* ─── State data ─── */
const STATE_DATA = {
  TX: { name: 'Texas', popGrowth: 2.5, jobGrowth: 4.2, rentGrowth: 7.8, protection: 88, score: 94 },
  FL: { name: 'Florida', popGrowth: 2.8, jobGrowth: 3.6, rentGrowth: 7.2, protection: 85, score: 92 },
  TN: { name: 'Tennessee', popGrowth: 1.7, jobGrowth: 3.0, rentGrowth: 6.1, protection: 83, score: 89 },
  NC: { name: 'North Carolina', popGrowth: 2.0, jobGrowth: 3.4, rentGrowth: 6.5, protection: 82, score: 87 },
  AZ: { name: 'Arizona', popGrowth: 2.1, jobGrowth: 3.2, rentGrowth: 5.8, protection: 81, score: 86 },
  GA: { name: 'Georgia', popGrowth: 1.9, jobGrowth: 3.1, rentGrowth: 6.0, protection: 80, score: 84 },
  SC: { name: 'South Carolina', popGrowth: 1.8, jobGrowth: 2.9, rentGrowth: 5.8, protection: 81, score: 83 },
  ID: { name: 'Idaho', popGrowth: 2.5, jobGrowth: 2.8, rentGrowth: 5.4, protection: 78, score: 81 },
  UT: { name: 'Utah', popGrowth: 2.3, jobGrowth: 3.5, rentGrowth: 5.6, protection: 79, score: 80 },
  NV: { name: 'Nevada', popGrowth: 1.9, jobGrowth: 2.5, rentGrowth: 5.2, protection: 76, score: 79 },
  OH: { name: 'Ohio', popGrowth: 0.2, jobGrowth: 1.6, rentGrowth: 4.8, protection: 78, score: 78 },
  IN: { name: 'Indiana', popGrowth: 0.6, jobGrowth: 1.8, rentGrowth: 4.3, protection: 79, score: 77 },
  AL: { name: 'Alabama', popGrowth: 0.4, jobGrowth: 1.2, rentGrowth: 4.1, protection: 72, score: 76 },
  MO: { name: 'Missouri', popGrowth: 0.4, jobGrowth: 1.5, rentGrowth: 4.0, protection: 75, score: 75 },
  AR: { name: 'Arkansas', popGrowth: 0.5, jobGrowth: 1.0, rentGrowth: 3.5, protection: 74, score: 74 },
  OK: { name: 'Oklahoma', popGrowth: 0.5, jobGrowth: 1.2, rentGrowth: 3.4, protection: 73, score: 73 },
  KY: { name: 'Kentucky', popGrowth: 0.4, jobGrowth: 1.3, rentGrowth: 3.8, protection: 71, score: 72 },
  VA: { name: 'Virginia', popGrowth: 0.9, jobGrowth: 2.0, rentGrowth: 4.4, protection: 69, score: 73 },
  KS: { name: 'Kansas', popGrowth: 0.2, jobGrowth: 0.9, rentGrowth: 2.9, protection: 68, score: 61 },
  MI: { name: 'Michigan', popGrowth: 0.3, jobGrowth: 1.4, rentGrowth: 4.5, protection: 74, score: 72 },
  CO: { name: 'Colorado', popGrowth: 1.4, jobGrowth: 2.4, rentGrowth: 4.9, protection: 62, score: 71 },
  MT: { name: 'Montana', popGrowth: 1.6, jobGrowth: 1.8, rentGrowth: 5.0, protection: 62, score: 65 },
  NE: { name: 'Nebraska', popGrowth: 0.4, jobGrowth: 1.1, rentGrowth: 3.1, protection: 69, score: 64 },
  IA: { name: 'Iowa', popGrowth: 0.3, jobGrowth: 1.0, rentGrowth: 3.0, protection: 70, score: 63 },
  PA: { name: 'Pennsylvania', popGrowth: 0.0, jobGrowth: 1.1, rentGrowth: 3.9, protection: 58, score: 62 },
  MN: { name: 'Minnesota', popGrowth: 0.6, jobGrowth: 1.3, rentGrowth: 3.2, protection: 51, score: 59 },
  WI: { name: 'Wisconsin', popGrowth: 0.2, jobGrowth: 1.1, rentGrowth: 3.1, protection: 62, score: 60 },
  DE: { name: 'Delaware', popGrowth: 0.8, jobGrowth: 1.1, rentGrowth: 3.4, protection: 56, score: 58 },
  MS: { name: 'Mississippi', popGrowth: -0.3, jobGrowth: 0.6, rentGrowth: 2.5, protection: 65, score: 55 },
  LA: { name: 'Louisiana', popGrowth: -0.2, jobGrowth: 0.7, rentGrowth: 2.8, protection: 60, score: 58 },
  NH: { name: 'New Hampshire', popGrowth: 0.7, jobGrowth: 1.4, rentGrowth: 4.3, protection: 53, score: 57 },
  ME: { name: 'Maine', popGrowth: 0.6, jobGrowth: 1.0, rentGrowth: 4.2, protection: 48, score: 52 },
  WY: { name: 'Wyoming', popGrowth: 0.3, jobGrowth: 0.6, rentGrowth: 2.6, protection: 70, score: 62 },
  SD: { name: 'South Dakota', popGrowth: 0.8, jobGrowth: 1.3, rentGrowth: 3.2, protection: 76, score: 69 },
  ND: { name: 'North Dakota', popGrowth: 0.5, jobGrowth: 0.8, rentGrowth: 2.4, protection: 65, score: 60 },
  WV: { name: 'West Virginia', popGrowth: -0.6, jobGrowth: 0.3, rentGrowth: 1.9, protection: 58, score: 50 },
  NM: { name: 'New Mexico', popGrowth: 0.3, jobGrowth: 0.8, rentGrowth: 3.0, protection: 52, score: 49 },
  IL: { name: 'Illinois', popGrowth: -0.5, jobGrowth: 0.9, rentGrowth: 3.1, protection: 35, score: 43 },
  MD: { name: 'Maryland', popGrowth: 0.4, jobGrowth: 1.2, rentGrowth: 3.5, protection: 46, score: 55 },
  OR: { name: 'Oregon', popGrowth: 0.4, jobGrowth: 1.6, rentGrowth: 3.3, protection: 24, score: 35 },
  NY: { name: 'New York', popGrowth: -0.6, jobGrowth: 0.9, rentGrowth: 2.8, protection: 15, score: 27 },
  CT: { name: 'Connecticut', popGrowth: 0.2, jobGrowth: 0.8, rentGrowth: 3.2, protection: 38, score: 41 },
  MA: { name: 'Massachusetts', popGrowth: 0.1, jobGrowth: 1.5, rentGrowth: 3.8, protection: 32, score: 44 },
  RI: { name: 'Rhode Island', popGrowth: 0.3, jobGrowth: 1.0, rentGrowth: 4.1, protection: 40, score: 46 },
  VT: { name: 'Vermont', popGrowth: 0.2, jobGrowth: 0.7, rentGrowth: 3.4, protection: 38, score: 43 },
  WA: { name: 'Washington', popGrowth: 0.8, jobGrowth: 2.2, rentGrowth: 3.9, protection: 30, score: 42 },
  CA: { name: 'California', popGrowth: -0.2, jobGrowth: 1.8, rentGrowth: 2.1, protection: 18, score: 29 },
  NJ: { name: 'New Jersey', popGrowth: 0.1, jobGrowth: 1.0, rentGrowth: 3.6, protection: 28, score: 36 },
  HI: { name: 'Hawaii', popGrowth: -0.4, jobGrowth: 1.2, rentGrowth: 2.5, protection: 22, score: 32 },
  AK: { name: 'Alaska', popGrowth: -0.3, jobGrowth: 0.4, rentGrowth: 1.8, protection: 45, score: 38 },
};


/* ─── Normal US map colors (green/yellow/orange/red visual style) ─── */
const STATE_MAP_COLORS = {
  AK: '#58d68d', AL: '#f47c2c', AR: '#f47c2c', AZ: '#f06423', CA: '#58d68d',
  CO: '#f06423', CT: '#58d68d', DE: '#9be64f', FL: '#e63b3f', GA: '#f06423',
  HI: '#58d68d', IA: '#f7c843', ID: '#f47c2c', IL: '#58d68d', IN: '#f47c2c',
  KS: '#f7c843', KY: '#f47c2c', LA: '#9be64f', MA: '#58d68d', MD: '#f06423',
  ME: '#9be64f', MI: '#f47c2c', MN: '#9be64f', MO: '#f47c2c', MS: '#f47c2c',
  MT: '#f7c843', NC: '#f06423', ND: '#f7c843', NE: '#f7c843', NH: '#9be64f',
  NJ: '#58d68d', NM: '#58d68d', NV: '#f47c2c', NY: '#58d68d', OH: '#f47c2c',
  OK: '#f47c2c', OR: '#58d68d', PA: '#f7c843', RI: '#58d68d', SC: '#f06423',
  SD: '#f7c843', TN: '#d9343f', TX: '#e63b3f', UT: '#f06423', VA: '#f06423',
  VT: '#9be64f', WA: '#58d68d', WI: '#f7c843', WV: '#9be64f', WY: '#f7c843',
};

function heatColor(score, code) {
  return STATE_MAP_COLORS[code] || '#f7c843';
}

function heatColorLight(score) {
  if (score >= 88) return '#dbeafe';
  if (score >= 80) return '#dbeafe';
  if (score >= 70) return '#dbeafe';
  if (score >= 60) return '#eff6ff';
  if (score >= 50) return '#eff6ff';
  return '#dbeafe';
}

/* ─── Pill helpers ─── */
function rPop(v) { if (v >= 2) return 'High'; if (v >= 1) return 'Strong'; if (v >= 0.3) return 'Moderate'; return 'Low'; }
function rJob(v) { if (v >= 3) return 'High'; if (v >= 2) return 'Strong'; if (v >= 1) return 'Good'; return 'Moderate'; }
function rRent(v) { if (v >= 6) return 'High'; if (v >= 5) return 'Strong'; if (v >= 4) return 'Good'; return 'Moderate'; }
function rProt(v) { if (v >= 85) return 'Excellent'; if (v >= 75) return 'Very Good'; if (v >= 65) return 'Good'; return 'Fair'; }

const PILL_STYLE = {
  High: { bg: '#dbeafe', color: '#1d4ed8' },
  Strong: { bg: '#dbeafe', color: '#1e40af' },
  Moderate: { bg: '#dbeafe', color: '#1e40af' },
  Good: { bg: '#dbeafe', color: '#1d4ed8' },
  Excellent: { bg: '#dbeafe', color: '#1e3a8a' },
  'Very Good': { bg: '#dbeafe', color: '#1d4ed8' },
  Low: { bg: '#dbeafe', color: '#1d4ed8' },
  Fair: { bg: '#dbeafe', color: '#1e3a8a' },
};

function Pill({ label }) {
  const s = PILL_STYLE[label] || { bg: '#f0f6ff', color: '#1e40af' };
  return (
    <span style={{
      background: s.bg, color: s.color, fontSize: '11px', fontWeight: 600,
      padding: '2px 8px', borderRadius: '12px', whiteSpace: 'nowrap'
    }}>
      {label}
    </span>
  );
}

function trendArrow(score) {
  if (score >= 75) return { icon: '↑', color: '#1d4ed8' };
  if (score >= 60) return { icon: '→', color: '#1e3a8a' };
  return { icon: '↓', color: '#0f2d5e' };
}

/* ─── Market intelligence text generator ─── */
function generateMI(code) {
  const d = STATE_DATA[code] || STATE_DATA['TX'];
  const name = d.name;
  const tier = d.score >= 88 ? 'Tier 1 — Top Market' : d.score >= 75 ? 'Tier 2 — Strong Market' : d.score >= 60 ? 'Tier 3 — Active Market' : 'Tier 4 — Developing Market';
  const rentLabel = d.rentGrowth >= 6 ? 'exceptional' : d.rentGrowth >= 5 ? 'strong' : d.rentGrowth >= 4 ? 'solid' : 'moderate';
  const popLabel = d.popGrowth >= 2 ? 'rapidly growing' : d.popGrowth >= 1 ? 'steadily growing' : d.popGrowth >= 0 ? 'stable' : 'declining';
  return {
    tier,
    grade: d.score >= 88 ? 'A+' : d.score >= 80 ? 'A' : d.score >= 70 ? 'B+' : d.score >= 60 ? 'B' : 'C',
    summary: `${name} is a ${tier.toLowerCase()} for multifamily investors with a composite score of ${d.score}/100. The state shows ${rentLabel} rent growth of +${d.rentGrowth}% YoY and a ${popLabel} population base.`,
    insights: [
      `📈 Rent growth of +${d.rentGrowth}% places ${name} in the ${d.rentGrowth >= 6 ? 'top 10%' : d.rentGrowth >= 5 ? 'top 20%' : 'top 35%'} of all states`,
      `👥 Population ${d.popGrowth >= 0 ? 'growing at +' : 'declining at '}${Math.abs(d.popGrowth)}% annually — ${d.popGrowth >= 1 ? 'strong renter demand' : 'monitor vacancy trends'}`,
      `💼 Job growth of +${d.jobGrowth}% signals ${d.jobGrowth >= 3 ? 'excellent' : 'solid'} employment expansion supporting rent collections`,
      `🛡️ Investor protection score: ${d.protection}/100 — ${d.protection >= 75 ? 'landlord-friendly laws with efficient eviction process' : 'review local regulations before finalizing underwriting'}`,
    ],
    recommendation: d.score >= 80
      ? ` PROCEED — ${name} market conditions strongly support continued underwriting and deal execution.`
      : d.score >= 60
        ? `⚠️ PROCEED WITH CAUTION — ${name} shows mixed signals. Stress-test rent assumptions and verify local submarket data.`
        : `🛑 ELEVATED RISK — ${name} has below-average investor metrics. Consider alternative markets before committing capital.`,
    capRateRange: d.score >= 80 ? '4.5% – 6.2%' : d.score >= 60 ? '5.5% – 7.0%' : '6.5% – 8.5%',
    avgVacancy: d.score >= 80 ? '4.8%' : d.score >= 60 ? '6.2%' : '8.1%',
  };
}

/* ─── Future Boom: per-signal city datasets ─────────────────────────────────
   Each AI signal button shows a different ranked view of the same cities,
   emphasising what's driving boom potential for that specific factor.
   ─────────────────────────────────────────────────────────────────────────── */
const BOOM_SIGNALS = {
  'Corporate Relocations': [
    { rank: 1, city: 'Dallas', state: 'TX', type: 'HQ Relocations', risk: 'Low', momentum: 97, desc: 'DFW absorbed 28+ corporate HQ moves in the last 24 months. Toyota, McKesson, and Goldman Sachs regional hubs are fueling high-income renter demand.' },
    { rank: 2, city: 'Austin', state: 'TX', type: 'HQ Relocations', risk: 'Low', momentum: 95, desc: 'Tesla, Oracle, and Apple expansions create high-earner renter pools. Tech relocation velocity remains the highest of any Sun Belt city.' },
    { rank: 3, city: 'Nashville', state: 'TN', type: 'HQ Relocations', risk: 'Low', momentum: 92, desc: 'Amazon, Oracle, and AllianceBernstein HQ relocations added 15,000+ jobs. Average income of relocating employees drives premium rent demand.' },
    { rank: 4, city: 'Charlotte', state: 'NC', type: 'HQ Relocations', risk: 'Low', momentum: 90, desc: 'LendingTree, Truist, and Honeywell regional expansions have driven consistent Class A and B+ multifamily absorption.' },
    { rank: 5, city: 'Phoenix', state: 'AZ', type: 'HQ Relocations', risk: 'Low', momentum: 88, desc: 'Intel, TSMC, and Nikola semiconductor corridor driving white-collar worker migration at scale into Phoenix metro.' },
    { rank: 6, city: 'Atlanta', state: 'GA', type: 'HQ Relocations', risk: 'Low', momentum: 86, desc: 'NCR, Airbnb, and Microsoft Southeast hubs are expanding. Airport logistics hub reinforces broad economic base.' },
    { rank: 7, city: 'Raleigh-Durham', state: 'NC', type: 'HQ Expansions', risk: 'Low', momentum: 84, desc: 'Apple Research Triangle campus ($1B investment) and Google expansions are drawing engineering talent, boosting Class A renter pool.' },
    { rank: 8, city: 'Tampa', state: 'FL', type: 'HQ Expansions', risk: 'Low', momentum: 82, desc: 'Raymond James, JPMorgan, and BDO expansions keep white-collar renter demand ahead of new supply.' },
    { rank: 9, city: 'Jacksonville', state: 'FL', type: 'Emerging HQ', risk: 'Medium', momentum: 76, desc: 'Fidelity and Deutsche Bank backoffice expansions adding employment base. Cap rates remain attractive at 6–7.5%.' },
    { rank: 10, city: 'Denver', state: 'CO', type: 'Tech Expansion', risk: 'Medium', momentum: 72, desc: 'Google, Salesforce, and Palantir Denver offices growing, but elevated supply pipeline requires careful submarket selection.' },
  ],
  'Infrastructure Projects': [
    { rank: 1, city: 'Phoenix', state: 'AZ', type: 'Transportation', risk: 'Low', momentum: 96, desc: '$3.8B light rail extension, I-10/SR-202 widening, and TSMC fab campus infrastructure are reshaping the metro\'s rental geography.' },
    { rank: 2, city: 'Dallas', state: 'TX', type: 'Transportation', risk: 'Low', momentum: 94, desc: 'DART Silver Line expansion and TEXRail corridor improvements opening new suburban rental markets ahead of supply.' },
    { rank: 3, city: 'Raleigh-Durham', state: 'NC', type: 'Tech Infrastructure', risk: 'Low', momentum: 92, desc: '$1B Apple campus + I-540 Outer Loop completion opening new multifamily corridors with minimal existing supply.' },
    { rank: 4, city: 'Nashville', state: 'TN', type: 'Transit', risk: 'Low', momentum: 90, desc: '$3.1B transit referendum funds underway. East Bank stadium district redevelopment creates new high-density rental zones.' },
    { rank: 5, city: 'Tampa', state: 'FL', type: 'Port & Transit', risk: 'Low', momentum: 88, desc: 'Port Tampa Bay expansion ($100M) and Brightline West rail connection in planning stage signal long-term demand drivers.' },
    { rank: 6, city: 'Atlanta', state: 'GA', type: 'BeltLine Expansion', risk: 'Low', momentum: 86, desc: 'Atlanta BeltLine Phase 2 completion is consistently driving 15–25% rent premium within 0.5-mile corridors.' },
    { rank: 7, city: 'Austin', state: 'TX', type: 'Tech Infrastructure', risk: 'Low', momentum: 84, desc: 'Project Connect light rail (Phase 1) and Tesla Gigafactory logistics corridor reshaping East Austin rental demand.' },
    { rank: 8, city: 'Charlotte', state: 'NC', type: 'LYNX Extension', risk: 'Low', momentum: 82, desc: 'LYNX Silver Line extension creates TOD multifamily opportunities 2–3 years ahead of institutional pricing.' },
    { rank: 9, city: 'Jacksonville', state: 'FL', type: 'Port Expansion', risk: 'Medium', momentum: 74, desc: 'Port Jacksonville tripling container capacity. Logistics employment base supports workforce housing demand at high cap rates.' },
    { rank: 10, city: 'Columbus', state: 'OH', type: 'Intel Fab Campus', risk: 'Medium', momentum: 70, desc: 'Intel $20B semiconductor campus is the largest job-creation event in Ohio history. Multifamily demand surge anticipated 2025–2027.' },
  ],
  'Migration Patterns': [
    { rank: 1, city: 'Tampa', state: 'FL', type: 'Net Inflow', risk: 'Low', momentum: 97, desc: 'Tampa leads all Florida metros in net in-migration. Remote workers from NY and CA driving rapid rent appreciation in Seminole Heights and Ybor City corridors.' },
    { rank: 2, city: 'Austin', state: 'TX', type: 'Net Inflow', risk: 'Low', momentum: 95, desc: 'Despite high home prices, Austin continues absorbing 150–200 net new residents daily. Rental demand from non-homebuyers remains elevated.' },
    { rank: 3, city: 'Nashville', state: 'TN', type: 'Net Inflow', risk: 'Low', momentum: 93, desc: 'Migration from Chicago, Detroit, and Northeast cities. No state income tax drives high-income renter retention compared to gateway markets.' },
    { rank: 4, city: 'Charlotte', state: 'NC', type: 'Net Inflow', risk: 'Low', momentum: 91, desc: 'Third fastest growing major metro for net domestic migration. Banking and tech sector absorbs white-collar inflow faster than new supply.' },
    { rank: 5, city: 'Jacksonville', state: 'FL', type: 'Net Inflow', risk: 'Low', momentum: 89, desc: 'Ranked #1 for affordability migration. Families priced out of Miami/Tampa finding Jacksonville a 20–25% rent discount with improving employment.' },
    { rank: 6, city: 'Raleigh-Durham', state: 'NC', type: 'Net Inflow', risk: 'Low', momentum: 87, desc: 'Research Triangle attracts STEM graduates nationally. 75% of UNC/Duke/NC State graduates remain in the metro, sustaining entry-level demand.' },
    { rank: 7, city: 'Phoenix', state: 'AZ', type: 'Net Inflow', risk: 'Low', momentum: 85, desc: 'California exodus continues at 80,000+ net annual migrants. Phoenix remains price-competitive versus coastal alternatives by a wide margin.' },
    { rank: 8, city: 'Atlanta', state: 'GA', type: 'Net Inflow', risk: 'Low', momentum: 83, desc: 'Southeast regional hub absorbing domestic migration at 60,000+ net annually. Diverse employment base reduces concentration risk.' },
    { rank: 9, city: 'Dallas', state: 'TX', type: 'Net Inflow', risk: 'Low', momentum: 81, desc: 'DFW net migration slowed from peak but remains strongly positive at 80,000+ annually. Suburban submarkets outperforming urban core.' },
    { rank: 10, city: 'Denver', state: 'CO', type: 'Slowing Inflow', risk: 'Medium', momentum: 68, desc: 'Migration tailwind weakening vs. 2020–2022 peak. Elevated cost of living and supply pipeline require submarket-level underwriting discipline.' },
  ],
  'Construction Permits': [
    { rank: 1, city: 'Columbus', state: 'OH', type: 'Undersupplied', risk: 'Low', momentum: 94, desc: 'Intel campus demand is NOT yet reflected in permits. Major supply gap expected 2025–2027 — early investors will benefit from compressing cap rates.' },
    { rank: 2, city: 'Jacksonville', state: 'FL', type: 'Controlled Supply', risk: 'Low', momentum: 91, desc: 'Permit activity is rising but absorption is outpacing new deliveries. Workforce housing segment has a 2-year supply gap.' },
    { rank: 3, city: 'Charlotte', state: 'NC', type: 'Controlled Supply', risk: 'Low', momentum: 89, desc: 'Permitting healthy but well-matched to absorption. No oversupply risk visible in current pipeline through 2026.' },
    { rank: 4, city: 'Nashville', state: 'TN', type: 'Moderate Supply', risk: 'Low', momentum: 87, desc: 'Pipeline is active but diversified across price points. Urban core seeing lease-up pressure; suburban submarkets absorbing cleanly.' },
    { rank: 5, city: 'Raleigh-Durham', state: 'NC', type: 'Moderate Supply', risk: 'Low', momentum: 85, desc: 'Permit growth tracking migration and job growth closely. Research Triangle Park expansion absorbing new supply ahead of delivery.' },
    { rank: 6, city: 'Tampa', state: 'FL', type: 'Moderate Supply', risk: 'Low', momentum: 83, desc: 'Supply pipeline elevated in some submarkets — confirm specific submarket absorption before underwriting aggressive Year 1 rents.' },
    { rank: 7, city: 'Atlanta', state: 'GA', type: 'Moderate Supply', risk: 'Low', momentum: 81, desc: 'Intown Atlanta (Midtown, Buckhead) showing concession pressure. Suburban OTP corridors significantly healthier on absorption metrics.' },
    { rank: 8, city: 'Dallas', state: 'TX', type: 'Elevated Supply', risk: 'Medium', momentum: 75, desc: 'DFW permit activity is high. Uptown/Frisco submarkets showing extended lease-up timelines. Deep submarket analysis required before commitment.' },
    { rank: 9, city: 'Austin', state: 'TX', type: 'Elevated Supply', risk: 'Medium', momentum: 70, desc: 'Austin has the highest per-capita delivery pipeline of any Sun Belt market. Stress-test rent growth assumptions — concessions are present in 2024.' },
    { rank: 10, city: 'Denver', state: 'CO', type: 'Oversupply Risk', risk: 'High', momentum: 58, desc: 'Denver\'s supply-to-absorption ratio is the most concerning of tracked markets. Selective micro-market underwriting is critical before deploying capital.' },
  ],
};

/* ─── Risk Radar — expanded to 12 markets with risk level for filtering ─── */
const RISK_DATA = [
  {
    state: 'Texas', city: 'Austin, TX', riskLevel: 'Low', overall: 'Low Risk', bar: 18,
    factors: [{ name: 'Supply Pipeline', level: 'Moderate', desc: 'Austin has the highest per-capita delivery pipeline in Texas — stress-test Year 1 rent assumptions and confirm submarket absorption before committing.' }, { name: 'Demand Shift', level: 'Low', desc: 'Long-term demand drivers are intact: migration, tech jobs, and UT Austin enrollment. Short-term lease-up timelines are extending.' }, { name: 'Expense Pressure', level: 'Low', desc: 'Property taxes, insurance, and payroll costs should be stress-tested but remain manageable relative to NOI.' }]
  },
  {
    state: 'Texas', city: 'Dallas-Fort Worth, TX', riskLevel: 'Low', overall: 'Low Risk', bar: 22,
    factors: [{ name: 'Supply Pipeline', level: 'Moderate', desc: 'DFW permit activity is elevated in Frisco and Uptown. Suburban B-class submarkets are absorbing cleanly — target those over luxury core.' }, { name: 'Demand Shift', level: 'Low', desc: 'Employment diversity across logistics, finance, and tech provides strong recession resistance.' }, { name: 'Expense Pressure', level: 'Low', desc: 'Texas has no rent control. Insurance and tax costs are rising but remain below coastal market averages.' }]
  },
  {
    state: 'Florida', city: 'Tampa, FL', riskLevel: 'Low', overall: 'Low Risk', bar: 20,
    factors: [{ name: 'Supply Pipeline', level: 'Low', desc: 'Tampa\'s pipeline is active but absorption rates remain healthy outside of downtown luxury tier.' }, { name: 'Demand Shift', level: 'Low', desc: 'Net in-migration remains among the highest in the US — sustained renter demand well ahead of supply.' }, { name: 'Expense Pressure', level: 'Moderate', desc: 'Hurricane insurance costs are materially elevated post-season — model a 15–20% insurance premium into your operating expense underwriting.' }]
  },
  {
    state: 'Florida', city: 'Jacksonville, FL', riskLevel: 'Low', overall: 'Low Risk', bar: 16,
    factors: [{ name: 'Supply Pipeline', level: 'Low', desc: 'Permit activity is rising but absorption is outpacing deliveries — supply gap expected through 2026.' }, { name: 'Demand Shift', level: 'Low', desc: 'Affordability migration from Miami and Tampa is accelerating net in-migration at above-average rates.' }, { name: 'Expense Pressure', level: 'Low', desc: 'Insurance costs below Tampa average and property taxes remain competitive — strong NOI protection environment.' }]
  },
  {
    state: 'Tennessee', city: 'Nashville, TN', riskLevel: 'Low', overall: 'Low Risk', bar: 22,
    factors: [{ name: 'Supply Pipeline', level: 'Moderate', desc: 'Urban core pipeline is active. East Bank stadium district deliveries may create short-term lease-up pressure in 2024–2025.' }, { name: 'Demand Shift', level: 'Low', desc: 'Diverse economy — healthcare, music industry, corporate HQs — provides recession-resistant renter base.' }, { name: 'Expense Pressure', level: 'Moderate', desc: 'Property tax increases in key Nashville submarkets (Germantown, The Nations) up 18% in last 24 months — underwrite carefully.' }]
  },
  {
    state: 'North Carolina', city: 'Charlotte, NC', riskLevel: 'Low', overall: 'Low Risk', bar: 20,
    factors: [{ name: 'Supply Pipeline', level: 'Low', desc: 'Permit growth is tracking migration and employment closely. No visible oversupply risk through 2026 in B/B+ submarkets.' }, { name: 'Demand Shift', level: 'Low', desc: 'Banking and fintech sector expansion continues to drive high-income renter demand and occupancy stability.' }, { name: 'Expense Pressure', level: 'Low', desc: 'Insurance and operating costs remain below national average — favorable NOI underwriting environment.' }]
  },
  {
    state: 'North Carolina', city: 'Raleigh-Durham, NC', riskLevel: 'Low', overall: 'Low Risk', bar: 25,
    factors: [{ name: 'Supply Pipeline', level: 'Low', desc: 'Construction pipeline is moderate relative to absorption capacity — university enrollment and tech hiring are filling units ahead of delivery.' }, { name: 'Demand Shift', level: 'Low', desc: 'Research Triangle\'s STEM employment base is among the most recession-resistant of any mid-size market nationally.' }, { name: 'Expense Pressure', level: 'Low', desc: 'Property taxes and insurance remain well below Sunbelt coastal markets — favorable for investor returns.' }]
  },
  {
    state: 'Georgia', city: 'Atlanta, GA', riskLevel: 'Medium', overall: 'Medium Risk', bar: 42,
    factors: [{ name: 'Supply Pipeline', level: 'High', desc: 'Intown Atlanta (Midtown, Buckhead, West Midtown) is oversupplied in Class A. Concessions are present — target suburban OTP corridors for B-class value-add.' }, { name: 'Demand Shift', level: 'Low', desc: 'Atlanta\'s employment base is broad and growing, but urban core lease-up pressure requires patience.' }, { name: 'Expense Pressure', level: 'Moderate', desc: 'Property taxes in Fulton and DeKalb counties rose sharply — reassess expense assumptions based on current assessed values.' }]
  },
  {
    state: 'Arizona', city: 'Phoenix, AZ', riskLevel: 'Medium', overall: 'Medium Risk', bar: 38,
    factors: [{ name: 'Supply Pipeline', level: 'High', desc: 'Phoenix had the highest multifamily deliveries of any US market in 2023 — confirm submarket absorption and expect extended lease-up in impacted areas.' }, { name: 'Demand Shift', level: 'Low', desc: 'TSMC and Intel campus construction is sustaining in-migration and employment growth that should absorb supply over 18–24 months.' }, { name: 'Expense Pressure', level: 'Moderate', desc: 'Insurance and utility costs rising. HOA-intensive communities require particular scrutiny on operating expense line items.' }]
  },
  {
    state: 'Colorado', city: 'Denver, CO', riskLevel: 'Medium', overall: 'Medium Risk', bar: 55,
    factors: [{ name: 'Supply Pipeline', level: 'High', desc: 'Denver\'s supply-to-absorption ratio is among the most elevated in the Sun Belt. Concessions are widespread in Class A. Target value-add B-class in supply-constrained submarkets.' }, { name: 'Demand Shift', level: 'Moderate', desc: 'In-migration has decelerated from 2020–2022 peak. Cost of living is reducing affordability advantage vs. gateway markets.' }, { name: 'Expense Pressure', level: 'High', desc: 'Property taxes are rising materially. Rent control discussions are active at the municipal level — monitor regulatory risk closely.' }]
  },
  {
    state: 'California', city: 'Los Angeles, CA', riskLevel: 'High', overall: 'High Risk', bar: 82,
    factors: [{ name: 'Supply Pipeline', level: 'High', desc: 'Chronic undersupply masked by extreme regulatory barriers. Entitled projects face 5–8 year timelines. Existing assets carry premium pricing with compressed returns.' }, { name: 'Demand Shift', level: 'High', desc: 'Net population outflow has been consistent for 4 consecutive years. Remote work continues reshaping urban rental demand fundamentals.' }, { name: 'Expense Pressure', level: 'High', desc: 'AB-1482 rent control caps NOI growth at CPI+5%. Combined with high insurance, property tax, and compliance costs — NOI upside is severely compressed.' }]
  },
  {
    state: 'New York', city: 'New York City, NY', riskLevel: 'High', overall: 'High Risk', bar: 88,
    factors: [{ name: 'Supply Pipeline', level: 'Medium', desc: 'Manhattan supply is constrained by cost and regulation. Outer borough (Brooklyn, Queens) pipelines are active with mixed absorption depending on submarket.' }, { name: 'Demand Shift', level: 'High', desc: 'Continued population loss and hybrid work patterns are reshaping which ZIP codes hold rental demand. Underwriting must be hyper-local.' }, { name: 'Expense Pressure', level: 'High', desc: 'Rent stabilization, HSTPA, LLC Transparency Act, and property taxes are the highest cost environment in the nation. Underwrite with extreme conservatism.' }]
  },
];

/* ─── US Geographic Choropleth Map (react-simple-maps + us-atlas TopoJSON) ─── */
function USMap({ selectedCode, onSelect, dealStateCode }) {
  const [tooltip, setTooltip] = useState({ visible: false, x: 0, y: 0, code: null });

  return (
    <div style={{ position: 'relative', background: '#e8f5fb', borderRadius: 10, overflow: 'hidden' }}>
      <ComposableMap
        projection="geoAlbersUsa"
        projectionConfig={{ scale: 900, translate: [488, 295] }}
        style={{ width: '100%', height: 'auto' }}
        viewBox="0 0 975 600"
      >
        <Geographies geography={GEO_URL}>
          {({ geographies }) =>
            geographies.map(geo => {
              const stateName = geo.properties.name;
              const code = NAME_TO_CODE[stateName];
              const data = code ? STATE_DATA[code] : null;
              const fill = data ? heatColor(data.score, code) : '#d6eaf2';
              const isSelected = code === selectedCode;
              const isDeal = code === dealStateCode;

              return (
                <Geography
                  key={geo.rsmKey}
                  geography={geo}
                  fill={fill}
                  stroke="#fff"
                  strokeWidth={isSelected ? 2.5 : isDeal ? 2 : 0.6}
                  style={{
                    default: {
                      outline: 'none',
                      opacity: isSelected ? 1 : 0.88,
                      filter: isDeal ? 'drop-shadow(0 0 4px rgba(15,118,110,0.7))' : 'none',
                    },
                    hover: { outline: 'none', opacity: 1, cursor: 'pointer', strokeWidth: 1.5 },
                    pressed: { outline: 'none' },
                  }}
                  onMouseEnter={e => {
                    if (!code) return;
                    const rect = e.currentTarget.closest('svg').getBoundingClientRect();
                    setTooltip({ visible: true, x: e.clientX - rect.left + 12, y: e.clientY - rect.top - 10, code });
                  }}
                  onMouseLeave={() => setTooltip({ visible: false, x: 0, y: 0, code: null })}
                  onClick={() => code && onSelect(isSelected ? null : code)}
                />
              );
            })
          }
        </Geographies>
      </ComposableMap>

      {/* Tooltip */}
      {tooltip.visible && tooltip.code && STATE_DATA[tooltip.code] && (
        <div className="m2-map-tooltip" style={{ left: tooltip.x, top: tooltip.y }}>
          <div className="m2-tt-name" style={{ color: '#ffffff' }}>{STATE_DATA[tooltip.code].name}</div>
          <div className="m2-tt-score" style={{ color: heatColor(STATE_DATA[tooltip.code].score, tooltip.code) }}>
            AcquireOS Score: <strong>{STATE_DATA[tooltip.code].score}</strong> / 100
          </div>
        </div>
      )}

      {/* Legend */}
      <div className="m2-map-legend">
        {[
          ['#58d68d', 'Healthy'],
          ['#9be64f', 'Stable'],
          ['#f7c843', 'Moderate'],
          ['#f47c2c', 'Active'],
          ['#e63b3f', 'High Watch'],
        ].map(([c, l]) => (
          <span key={l} className="m2-legend-item">
            <span style={{ background: c, width: 12, height: 12, borderRadius: 2, display: 'inline-block' }} />
            {l}
          </span>
        ))}
      </div>
    </div>
  );
}

/* ─── State detail panel ─── */
function StateDetailPanel({ code, onClose }) {
  const data = STATE_DATA[code];
  if (!data) return null;
  const topTag = data.score >= 90 ? 'Top Market' : data.score >= 80 ? 'Strong Market' : 'Active Market';
  const metrics = [
    { label: 'Population Growth', value: rPop(data.popGrowth), desc: 'Measures how many people are moving into this state. More people means more renters, higher housing demand, and lower vacancy rates.' },
    { label: 'Job Growth', value: rJob(data.jobGrowth), desc: 'Tracks new employment opportunities. People move where jobs exist — more jobs create higher incomes, stronger rent collections, and better occupancy.' },
    { label: 'Rent Growth', value: rRent(data.rentGrowth), desc: 'Tracks how fast rents are increasing. Higher rent growth means better NOI trajectory and stronger asset appreciation over time.' },
    { label: 'Investor Protection', value: rProt(data.protection), desc: 'Rates landlord-friendly laws, eviction timelines, and regulatory environment. Higher protection means fewer surprises and better cash flow reliability.' },
  ];
  return (
    <div className="m2-detail-panel">
      <div className="m2-detail-header">
        <div>
          <div className="m2-detail-name">{data.name}</div>
          <div className="m2-detail-badge" style={{ background: heatColorLight(data.score), color: heatColor(data.score) }}>
            {topTag} · {data.score}/100
          </div>
        </div>
        <button className="m2-detail-close" onClick={onClose}>✕</button>
      </div>
      {metrics.map(m => (
        <div key={m.label} className="m2-detail-metric">
          <div className="m2-detail-metric-top">
            <span className="m2-detail-metric-label">{m.label}</span>
            <Pill label={m.value} />
          </div>
          <p className="m2-detail-metric-desc">{m.desc}</p>
        </div>
      ))}
      <div className="m2-detail-sources">
        <strong>Sources</strong>
        <span>Population: {data.dataSources?.population || 'Editorial fallback'}</span>
        <span>Jobs: {data.dataSources?.jobs || 'Editorial fallback'}</span>
        <span>Rent: {data.dataSources?.rent || 'LoopNet overlay / editorial fallback'}</span>
        <span>Protection: {data.dataSources?.protection || 'Editorial legal/regulatory score'}</span>
      </div>
    </div>
  );
}

/* ─── Deal Assumptions + Generate Market Intelligence ─── */
function buildGeneratedDealAssumptions(dealState) {
  const raw = dealState?.rawDealData || {};
  const fin = dealState?.extractedFinancials || {};

  const rawPrice = Number(raw.price) || 0;
  const knownUnits =
    Number(raw.property?.units) ||
    Number(fin.revenue?.rentRoll?.totalUnits) ||
    0;
  const units = knownUnits || (rawPrice ? Math.max(1, Math.round(rawPrice / 150000)) : 24);
  const occupancy = Number(fin.occupancy) || Number(fin.revenue?.rentRoll?.occupancyRate) || 94.2;
  const averageRent = Number(fin.revenue?.rentRoll?.averageRent) || 1450;

  const generatedRevenue = Math.round(units * averageRent * 12 * (occupancy / 100));
  const revenue = Number(fin.revenue?.totalRevenue) || generatedRevenue;
  const existingExpenses =
    Number(fin.expenses?.totalExpenses) ||
    Number(fin.expenses?.totalOperatingExpenses) ||
    0;
  const generatedNoi = rawPrice ? Math.round(rawPrice * 0.065) : Math.round(revenue * 0.58);
  const noi = Number(fin.noi) || generatedNoi;
  const expenses = existingExpenses || Math.max(0, revenue - noi);

  return {
    units,
    revenue,
    expenses,
    noi,
    occupancy,
    averageRent: Math.round(revenue / 12 / Math.max(units, 1)),
    occupiedUnits: Math.round(units * (occupancy / 100)),
  };
}

function DealAssumptions({ dealState, onContinue }) {
  const [miLoading, setMiLoading] = useState(false);
  const [miData, setMiData] = useState(null);

  const { revenue, expenses, noi, occupancy } = buildGeneratedDealAssumptions(dealState);
  const validation = 92;

  // Find deal state code
  const dealCode = useMemo(() => {
    const st = dealState?.rawDealData?.location?.state;
    if (!st) return 'TX';
    return Object.keys(STATE_DATA).find(k => STATE_DATA[k].name === st) || 'TX';
  }, [dealState]);

  const handleGenerate = () => {
    setMiLoading(true);
    setMiData(null);
    setTimeout(() => {
      setMiLoading(false);
      setMiData(generateMI(dealCode));
    }, 1600);
  };

  const riskColor = l => l === 'Low' ? '#1d4ed8' : l === 'Medium' || l === 'Moderate' ? '#1e3a8a' : '#0f2d5e';

  return (
    <div className="m2-assumptions">
      <div className="m2-assumptions-title">Generated Deal Assumptions</div>
      <div className="m2-assumptions-grid">
        <div className="m2-assumption-item">
          <div className="m2-assumption-label">Total Revenue</div>
          <div className="m2-assumption-value">${revenue.toLocaleString()}</div>
        </div>
        <div className="m2-assumption-item">
          <div className="m2-assumption-label">Total Expenses</div>
          <div className="m2-assumption-value">${expenses.toLocaleString()}</div>
        </div>
        <div className="m2-assumption-item">
          <div className="m2-assumption-label">NOI</div>
          <div className="m2-assumption-value" style={{ color: '#1d4ed8' }}>${noi.toLocaleString()}</div>
        </div>
        <div className="m2-assumption-item">
          <div className="m2-assumption-label">Occupancy</div>
          <div className="m2-assumption-value" style={{ color: '#1d4ed8' }}>{occupancy}%</div>
        </div>
      </div>

      <div className="m2-validation">
        <div className="m2-validation-label">Validation</div>
        <div className="m2-validation-pct">{validation}% complete</div>
        <div className="m2-validation-bar-track">
          <div className="m2-validation-bar-fill" style={{ width: `${validation}%` }} />
        </div>
        <div className="m2-validation-msg">Market supports continued underwriting</div>
      </div>

      <button className={`m2-gen-btn ${miLoading ? 'loading' : ''}`} onClick={handleGenerate} disabled={miLoading}>
        {miLoading ? (
          <span className="m2-gen-spinner">
            <span className="m2-spin">⟳</span> Analyzing Market…
          </span>
        ) : (
          miData ? '🔄 Regenerate Market Intelligence' : 'Generate Market Intelligence'
        )}
      </button>

      {/* Market Intelligence Output */}
      {miData && (
        <div className="m2-mi-output">
          <div className="m2-mi-header">
            <div className="m2-mi-grade" style={{ background: heatColor(STATE_DATA[dealCode]?.score || 80) }}>
              {miData.grade}
            </div>
            <div>
              <div className="m2-mi-tier">{miData.tier}</div>
              <div className="m2-mi-state-label">{STATE_DATA[dealCode]?.name || 'Texas'}</div>
            </div>
          </div>
          <p className="m2-mi-summary">{miData.summary}</p>
          <div className="m2-mi-metrics-row">
            <div className="m2-mi-metric-box">
              <div className="m2-mi-metric-val">Cap Rate</div>
              <div className="m2-mi-metric-num">{miData.capRateRange}</div>
            </div>
            <div className="m2-mi-metric-box">
              <div className="m2-mi-metric-val">Avg Vacancy</div>
              <div className="m2-mi-metric-num">{miData.avgVacancy}</div>
            </div>
          </div>
          <div className="m2-mi-insights">
            {miData.insights.map((ins, i) => (
              <div key={i} className="m2-mi-insight">{ins}</div>
            ))}
          </div>
          <div className={`m2-mi-rec ${miData.recommendation.startsWith('') ? 'rec-blue-strong' : miData.recommendation.startsWith('⚠️') ? 'rec-blue-mid' : 'rec-blue-deep'}`}>
            {miData.recommendation}
          </div>
        </div>
      )}

    </div>
  );
}

/* ─── Workflow Tab ─── */
function WorkflowTab({ dealState, selectedState, setSelectedState, onContinue, onViewAllStates }) {
  const sorted = Object.entries(STATE_DATA).sort((a, b) => b[1].score - a[1].score).slice(0, 6);
  const dealStateCode = useMemo(() => {
    return normalizeStateCode(dealState?.rawDealData?.location?.state);
  }, [dealState]);

  return (
    <div className="m2-workflow-layout">
      <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        {/* Geographic map */}
        <div className="m2-panel">
          <div className="m2-panel-hdr">
            <span>🗺️</span>
            <div>
              <h3>50-State AcquireOS Market Score</h3>
              <p>Composite score hydrated from Census/BLS where available, with clearly labeled fallback fields · Red = strongest · Click any state</p>
            </div>
          </div>
          <USMap
            selectedCode={selectedState}
            onSelect={setSelectedState}
            dealStateCode={dealStateCode}
          />
        </div>

        {/* Selected state detail */}
        {selectedState && (
          <StateDetailPanel code={selectedState} onClose={() => setSelectedState(null)} />
        )}
      </div>

      <div className="m2-workflow-right">
        {/* Top investor friendly states */}
        <div className="m2-panel">
          <div className="m2-panel-hdr"><span>🏆</span><h3>Top Investor Friendly States</h3></div>
          <div className="m2-top-list">
            {sorted.map(([code, d], i) => (
              <div key={code} className="m2-top-row" onClick={() => setSelectedState(selectedState === code ? null : code)}>
                <span className="m2-top-rank">{i + 1}</span>
                <span className="m2-top-name">{d.name}</span>
                <span className="m2-top-score" style={{ color: heatColor(d.score) }}>{d.score}</span>
              </div>
            ))}
          </div>
          <div className="m2-view-all" onClick={onViewAllStates} style={{ cursor: 'pointer' }}>View All 50 States →</div>
        </div>

        {/* Trend chart */}
        <div className="m2-panel">
          <div className="m2-panel-hdr"><span>📈</span><h3>Trend Over Time</h3></div>
          <div className="m2-trend-chart">
            {[{ y: '2019', h: 55 }, { y: '2020', h: 48 }, { y: '2021', h: 65 }, { y: '2022', h: 78 }, { y: '2023', h: 88 }, { y: '2024', h: 95 }].map(b => (
              <div key={b.y} className="m2-trend-bar-wrap">
                <div className="m2-trend-bar" style={{ height: `${b.h}%` }} />
                <span className="m2-trend-year">{b.y}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Data refresh */}
        <div className="m2-panel">
          <div className="m2-panel-hdr"><span>🔄</span><h3>Data Refresh Frequency</h3></div>
          <div className="m2-refresh-list">
            {[['Population data', 'Monthly', '#1d4ed8'], ['Job and economy data', 'Monthly', '#1d4ed8'], ['Rent data', 'Weekly', '#2563eb'], ['Investor protection data', 'As needed', '#1e40af'], ['Market signals', 'Daily', '#3b82f6']].map(([label, freq, color]) => (
              <div key={label} className="m2-refresh-row">
                <span className="m2-refresh-label">{label}</span>
                <span className="m2-refresh-freq" style={{ color }}>{freq}</span>
              </div>
            ))}
          </div>
        </div>

      </div>
    </div>
  );
}

/* ─── 50-State Score Tab ─── */
function ScoreTab({ dealState, dealStateCode, liveStateStats, publicScoreSource, onContinue }) {
  const [search, setSearch] = useState('');
  const [selectedCode, setSelectedCode] = useState(null);

  const filtered = useMemo(() => {
    const q = search.toLowerCase();
    return Object.entries(STATE_DATA)
      .filter(([code, d]) => !q || d.name.toLowerCase().includes(q) || code.toLowerCase().includes(q))
      .sort((a, b) => b[1].score - a[1].score);
  }, [search]);

  return (
    <div className="m2-score-layout">
      <div className="m2-panel m2-score-panel">
        <div className="m2-score-head">
          <div>
            <h3>50-State AcquireOS Market Score</h3>
            <p>Public-source composite: Census population growth + BLS employment growth where available; LoopNet overlays active deal market data.</p>
            <div className="m2-score-source-mini">
              {publicScoreSource.loading ? 'Loading Census/BLS...' : `Population: ${publicScoreSource.population} · Jobs: ${publicScoreSource.jobs}`}
            </div>
          </div>
          <input className="m2-search-input" placeholder="Search state" value={search} onChange={e => setSearch(e.target.value)} />
        </div>
        <div className="m2-table-header">
          <span className="col-state">STATE</span>
          <span className="col-pill">POPULATION</span>
          <span className="col-pill">JOBS</span>
          <span className="col-pill">RENT</span>
          <span className="col-pill">PROTECTION</span>
          <span className="col-score">SCORE</span>
          <span className="col-trend">TREND</span>
        </div>
        <div className="m2-table-body">
          {filtered.map(([code, d]) => {
            const tr = trendArrow(d.score);
            const isActive = selectedCode === code;
            // For the deal's own state, overlay LoopNet market cap rate if available
            const isDealState = liveStateStats?.isLive && code === dealStateCode;
            const liveCapRate = isDealState && Number.isFinite(Number(liveStateStats?.avgCapRate))
              ? Number(liveStateStats.avgCapRate)
              : null;
            const liveRent = liveCapRate; // backward-compat alias for the row below
            return (
              <div key={code} className={`m2-table-row ${isActive ? 'active-row' : ''}`} onClick={() => setSelectedCode(isActive ? null : code)}>
                <span className="col-state">
                  <span className="m2-state-badge" style={{ background: heatColor(d.score) }}>{code}</span>
                  <span className="m2-state-fullname">{d.name}</span>
                  {d.score >= 88 && <span className="m2-top-pick">★ Top Pick</span>}
                </span>
                <span className="col-pill"><Pill label={rPop(d.popGrowth)} /></span>
                <span className="col-pill"><Pill label={rJob(d.jobGrowth)} /></span>
                <span className="col-pill">
                  {liveRent != null
                    ? <span className="m2-live-pill">
                      <span className="m2-live-dot-sm" />
                      {liveRent.toFixed(1)}% LIVE
                    </span>
                    : <Pill label={rRent(d.rentGrowth)} />
                  }
                </span>
                <span className="col-pill"><Pill label={rProt(d.protection)} /></span>
                <span className="col-score">
                  <span className="m2-score-circle" style={{ background: heatColor(d.score) }}>{d.score}</span>
                  <span className="m2-score-mini-bar-track">
                    <span className="m2-score-mini-bar" style={{ width: `${d.score}%`, background: heatColor(d.score) }} />
                  </span>
                </span>
                <span className="col-trend" style={{ color: tr.color, fontSize: 16, fontWeight: 700 }}>{tr.icon}</span>
              </div>
            );
          })}
        </div>
      </div>
      <div className="m2-right-stack">
        {selectedCode
          ? <><StateDetailPanel code={selectedCode} onClose={() => setSelectedCode(null)} /><DealAssumptions dealState={dealState} onContinue={onContinue} /></>
          : <DealAssumptions dealState={dealState} onContinue={onContinue} />
        }
      </div>
    </div>
  );
}

/* ─── Future Boom Tab ─── */
const SIGNAL_META = {
  'Corporate Relocations': { icon: '🏢', color: '#1d4ed8', bg: '#dbeafe', desc: 'Tracks major company HQ and regional office moves that create sustained high-income renter demand.' },
  'Infrastructure Projects': { icon: '🏗️', color: '#1e40af', bg: '#dbeafe', desc: 'Identifies large-scale infrastructure investments that open new rental corridors ahead of supply.' },
  'Migration Patterns': { icon: '🌐', color: '#2563eb', bg: '#dbeafe', desc: 'Analyzes net domestic in-migration flows to predict where rental demand will grow the fastest.' },
  'Construction Permits': { icon: '📋', color: '#1e40af', bg: '#dbeafe', desc: 'Monitors permit-to-absorption ratios to flag both supply gaps (opportunity) and oversupply risk (caution).' },
};

function FutureBoomTab({ dealState, onContinue }) {
  const [activeSignal, setActiveSignal] = useState('Corporate Relocations');
  const signals = Object.keys(SIGNAL_META);
  const cities = BOOM_SIGNALS[activeSignal] || [];
  const meta = SIGNAL_META[activeSignal];

  const riskColors = {
    Low: { bg: '#dbeafe', color: '#1d4ed8' },
    Medium: { bg: '#dbeafe', color: '#1e40af' },
    High: { bg: '#dbeafe', color: '#1e3a8a' },
  };

  return (
    <div className="m2-score-layout">
      <div className="m2-panel m2-score-panel">
        <div className="m2-boom-head">
          <h3>🔭 Future Boom Predictor</h3>
          <p>AI identifies markets poised to boom <em>before</em> institutional investors arrive. Select an AI signal below to see a different ranked view of upcoming opportunity cities.</p>
        </div>

        {/* Signal selector */}
        <div className="m2-signal-row">
          <span className="m2-signal-label">AI SIGNAL:</span>
          {signals.map(s => {
            const m = SIGNAL_META[s];
            return (
              <button
                key={s}
                className={`m2-signal-btn ${activeSignal === s ? 'active' : ''}`}
                style={activeSignal === s ? { background: m.bg, color: m.color, borderColor: m.color } : {}}
                onClick={() => setActiveSignal(s)}
              >
                {m.icon} {s}
              </button>
            );
          })}
        </div>

        {/* Active signal description */}
        <div className="m2-signal-desc" style={{ background: meta.bg, color: meta.color }}>
          <strong>{meta.icon} {activeSignal}:</strong> {meta.desc}
        </div>

        {/* City list — re-renders on signal change */}
        <div className="m2-boom-list">
          {cities.map(c => {
            const rc = riskColors[c.risk] || riskColors.Low;
            return (
              <div key={`${activeSignal}-${c.rank}`} className="m2-boom-item">
                <div className="m2-boom-rank">#{c.rank}</div>
                <div className="m2-boom-content">
                  <div className="m2-boom-title-row">
                    <span className="m2-boom-city">{c.city}, {c.state}</span>
                    <span className="m2-boom-type" style={{ background: rc.bg, color: rc.color }}>{c.type}</span>
                    <span className="m2-boom-risk" style={{ color: rc.color }}>Risk: {c.risk}</span>
                  </div>
                  <p className="m2-boom-desc">{c.desc}</p>
                  <div className="m2-momentum-row">
                    <span className="m2-momentum-label">{activeSignal.toUpperCase()} MOMENTUM</span>
                    <div className="m2-momentum-track">
                      <div
                        className="m2-momentum-fill"
                        style={{ width: `${c.momentum}%`, background: c.momentum >= 90 ? '#1d4ed8' : c.momentum >= 75 ? '#3b82f6' : '#1e3a8a' }}
                      />
                    </div>
                    <span className="m2-momentum-val">{c.momentum}/100</span>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
      <div className="m2-right-stack"><DealAssumptions dealState={dealState} onContinue={onContinue} /></div>
    </div>
  );
}

/* ─── Risk Radar Tab ─── */
function RiskRadarTab({ dealState, onContinue }) {
  const [expanded, setExpanded] = useState({});
  const [riskFilter, setRiskFilter] = useState('All'); // All | Low | Medium | High
  const [sortBy, setSortBy] = useState('risk');  // risk | alpha

  const toggle = key => setExpanded(p => ({ ...p, [key]: !p[key] }));

  const riskColor = l => l === 'Low' ? '#1d4ed8' : l === 'Medium' || l === 'Moderate' ? '#1e3a8a' : '#0f2d5e';
  const riskBg = l => l === 'Low' ? '#dbeafe' : l === 'Medium' || l === 'Moderate' ? '#dbeafe' : '#dbeafe';
  const riskOrder = { Low: 0, Medium: 1, High: 2 };

  const filtered = useMemo(() => {
    let list = [...RISK_DATA];
    if (riskFilter !== 'All') list = list.filter(r => r.riskLevel === riskFilter);
    if (sortBy === 'risk') list.sort((a, b) => (riskOrder[b.riskLevel] ?? 0) - (riskOrder[a.riskLevel] ?? 0));
    if (sortBy === 'alpha') list.sort((a, b) => a.city.localeCompare(b.city));
    return list;
  }, [riskFilter, sortBy]);

  const counts = useMemo(() => ({
    All: RISK_DATA.length,
    Low: RISK_DATA.filter(r => r.riskLevel === 'Low').length,
    Medium: RISK_DATA.filter(r => r.riskLevel === 'Medium').length,
    High: RISK_DATA.filter(r => r.riskLevel === 'High').length,
  }), []);

  return (
    <div className="m2-score-layout">
      <div className="m2-panel m2-score-panel">
        <div className="m2-boom-head">
          <h3>📡 Market Risk Radar</h3>
          <p>AI monitors early warning signals across all major markets: supply pipeline, demand shifts, expense pressure, and regulatory risk. Use the filter to focus on what matters to your strategy.</p>
        </div>

        {/* Filter + sort bar */}
        <div className="m2-radar-controls">
          <div className="m2-radar-filter-group">
            <span className="m2-signal-label">RISK LEVEL:</span>
            {['All', 'Low', 'Medium', 'High'].map(lvl => (
              <button
                key={lvl}
                className={`m2-radar-filter-btn ${riskFilter === lvl ? 'active' : ''}`}
                style={riskFilter === lvl && lvl !== 'All'
                  ? { background: riskBg(lvl), color: riskColor(lvl), borderColor: riskColor(lvl) }
                  : riskFilter === lvl ? { background: '#1d4ed8', color: '#fff', borderColor: '#1d4ed8' } : {}
                }
                onClick={() => setRiskFilter(lvl)}
              >
                {lvl === 'Low' ? '🟢' : lvl === 'Medium' ? '🟡' : lvl === 'High' ? '🔴' : '📋'} {lvl}
                <span className="m2-radar-count">{counts[lvl]}</span>
              </button>
            ))}
          </div>
          <div className="m2-radar-sort">
            <span className="m2-signal-label">SORT:</span>
            <select
              className="m2-radar-sort-select"
              value={sortBy}
              onChange={e => setSortBy(e.target.value)}
            >
              <option value="risk">Highest Risk First</option>
              <option value="alpha">City (A–Z)</option>
            </select>
          </div>
        </div>

        {/* Results count */}
        <div className="m2-radar-results-label">
          Showing {filtered.length} market{filtered.length !== 1 ? 's' : ''}
          {riskFilter !== 'All' ? ` · ${riskFilter} Risk only` : ''}
        </div>

        {/* Market list */}
        <div className="m2-risk-list">
          {filtered.map(r => {
            const key = r.city;
            const isOpen = expanded[key];
            return (
              <div key={key} className="m2-risk-item">
                <div className="m2-risk-header" onClick={() => toggle(key)}>
                  <div>
                    <div className="m2-risk-state">{r.state}</div>
                    <div className="m2-risk-city">{r.city}</div>
                  </div>
                  <div className="m2-risk-right">
                    <span className="m2-risk-badge"
                      style={{ background: riskBg(r.riskLevel), color: riskColor(r.riskLevel) }}>
                      {r.overall}
                    </span>
                    <span style={{ color: '#1e40af', marginLeft: 8 }}>{isOpen ? '▲' : '▼'}</span>
                  </div>
                </div>

                {/* Risk bar */}
                <div className="m2-risk-bar-track">
                  <div className="m2-risk-bar-fill"
                    style={{ width: `${r.bar}%`, background: riskColor(r.riskLevel) }} />
                </div>
                <div className="m2-risk-bar-labels">
                  <span style={{ color: '#1d4ed8', fontSize: 10 }}>Low Risk</span>
                  <span style={{ color: '#0f2d5e', fontSize: 10 }}>High Risk</span>
                </div>

                {isOpen && (
                  <div className="m2-risk-factors">
                    {r.factors.map(f => (
                      <div key={f.name} className="m2-risk-factor">
                        <div className="m2-risk-factor-header">
                          <span className="m2-risk-dot" style={{ background: riskColor(f.level) }} />
                          <span className="m2-risk-factor-name">{f.name}</span>
                          <span className="m2-risk-factor-badge"
                            style={{ background: riskBg(f.level), color: riskColor(f.level) }}>
                            {f.level}
                          </span>
                        </div>
                        <p className="m2-risk-factor-desc">{f.desc}</p>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })}

          {filtered.length === 0 && (
            <div style={{ textAlign: 'center', padding: '40px 20px', color: '#1e40af' }}>
              No markets match the selected risk level.
            </div>
          )}
        </div>
      </div>
      <div className="m2-right-stack"><DealAssumptions dealState={dealState} onContinue={onContinue} /></div>
    </div>
  );
}

/* ─── Main Module 2 Page ─── */
function Module2Page() {
  const { dealState, updateDealState, updateAvailableModules, forceNavigateToModule } = useDealStore();
  const [showOmRequiredPopup, setShowOmRequiredPopup] = useState(false);
  // ── Compute & save extractedFinancials, unlock next modules, then navigate ──
  const handleComplete = () => {
    if (!dealState.financialDocuments?.omUploaded) {
      setShowOmRequiredPopup(true);
      return;
    }

    const { units, revenue, expenses, noi, occupancy, averageRent, occupiedUnits } =
      buildGeneratedDealAssumptions(dealState);

    // Resolve the deal's state code for market intelligence
    const dealCode = normalizeStateCode(dealState.rawDealData?.location?.state);
    const mi = generateMI(dealCode);
    const stData = STATE_DATA[dealCode] || STATE_DATA['TX'];

    updateDealState({
      pipelineStage: Math.max(dealState.pipelineStage || 1, 2),
      pipelineStatus: 'in_progress',
      extractedFinancials: {
        revenue: {
          grossPotentialRent: revenue,
          otherIncome: 0,
          totalRevenue: revenue,
          rentRoll: {
            totalUnits: units || null,
            occupiedUnits: occupiedUnits || null,
            occupancyRate: occupancy,
            averageRent: averageRent || null,
            unitMix: [],
          },
        },
        expenses: {
          operatingExpenses: {
            propertyTaxes: Math.round(expenses * 0.22),
            insurance: Math.round(expenses * 0.10),
            repairs: Math.round(expenses * 0.12),
            maintenance: Math.round(expenses * 0.08),
            utilities: Math.round(expenses * 0.10),
            propertyManagement: Math.round(expenses * 0.20),
            marketing: Math.round(expenses * 0.04),
            administrative: Math.round(expenses * 0.06),
            other: Math.round(expenses * 0.08),
          },
          totalOperatingExpenses: expenses,
          capitalExpenditures: 0,
          totalExpenses: expenses,
        },
        noi,
        occupancy,
        dataValidation: {
          missingFields: [],
          alerts: [],
          dataQuality: 'high',
          completeness: 92,
        },
        marketIntelligenceDetails: {
          marketRent: averageRent || null,
          marketOccupancy: occupancy,
          comparableProperties: [],
          marketTrends: mi.summary,
          stateScore: stData.score,
          stateTier: mi.tier,
          stateGrade: mi.grade,
          capRateRange: mi.capRateRange,
          avgVacancy: mi.avgVacancy,
          recommendation: mi.recommendation,
          insights: mi.insights,
        },
      },
    });

    updateAvailableModules();
    forceNavigateToModule(3);
  };

  const [activeTab, setActiveTab] = useState('workflow');
  const [selectedState, setSelectedState] = useState(null);
  const [publicScoreSource, setPublicScoreSource] = useState({
    loading: true,
    population: 'Loading Census PEP',
    jobs: 'Loading BLS LAUS',
    rent: 'LoopNet overlay for active deal state',
  });

  useEffect(() => {
    let cancelled = false;
    fetchPublicStateScores(STATE_DATA).then(result => {
      if (cancelled) return;
      if (result?.states) {
        Object.assign(STATE_DATA, result.states);
        setPublicScoreSource({ loading: false, ...result.sources });
      } else {
        setPublicScoreSource({
          loading: false,
          population: 'Unavailable; showing editorial fallback',
          jobs: 'Unavailable; showing editorial fallback',
          rent: 'LoopNet overlay for active deal state; fallback table elsewhere',
        });
      }
    });
    return () => { cancelled = true; };
  }, []);

  // ── Live market data ──────────────────────────────────────────────────────
  const dealStateCode = normalizeStateCode(dealState?.rawDealData?.location?.state || selectedState);
  const { macro: liveMacro, stateStats: liveStateStats } = useMarketData(dealStateCode);

  const tabs = [
    { id: 'workflow', label: 'Workflow' },
    { id: 'score', label: '50-State Score' },
    { id: 'boom', label: 'Future Boom' },
    { id: 'risk', label: 'Risk Radar' },
  ];

  return (
    <div className="m2-page">
      {/* Deal context bar */}
      {dealState.dealId && (
        <div className="m2-deal-bar">
          <span className="m2-deal-bar-name">{dealState.dealName}</span>
          {dealState.rawDealData?.price && (
            <span className="m2-deal-bar-price">Price: <strong>${Number(dealState.rawDealData.price).toLocaleString()}</strong></span>
          )}
          {dealState.rawDealData?.location?.address && (
            <span className="m2-deal-bar-addr">📍 {dealState.rawDealData.location.address}</span>
          )}
          <span className="m2-deal-bar-stage">CURRENT STAGE: <strong>Market Intelligence</strong></span>
          <span className="m2-deal-bar-status">IN PROGRESS</span>
        </div>
      )}

      {/* Tab nav */}
      <div className="m2-tabs">
        <div className="m2-tabs-inner">
          {tabs.map(t => (
            <button key={t.id} className={`m2-tab ${activeTab === t.id ? 'active' : ''}`} onClick={() => setActiveTab(t.id)}>
              {t.label}
            </button>
          ))}
        </div>
        <span className="m2-module-badge">MODULE 2 · Market Intelligence</span>
      </div>

      {/* Content */}
      <div className="m2-content">
        {activeTab === 'workflow' && <WorkflowTab dealState={dealState} selectedState={selectedState} setSelectedState={setSelectedState} onContinue={handleComplete} onViewAllStates={() => setActiveTab('score')} />}
        {activeTab === 'score' && <ScoreTab dealState={dealState} dealStateCode={dealStateCode} liveStateStats={liveStateStats} publicScoreSource={publicScoreSource} onContinue={handleComplete} />}
        {activeTab === 'boom' && <FutureBoomTab dealState={dealState} onContinue={handleComplete} />}
        {activeTab === 'risk' && <RiskRadarTab dealState={dealState} liveStateStats={liveStateStats} liveMacro={liveMacro} onContinue={handleComplete} />}
        <div className="m2-live-pulse-wrap">
          <LiveMarketPulse
            stateCode={dealStateCode}
            compact={true}
          />
        </div>
      </div>
      {showOmRequiredPopup && (
        <div className="m2-popup-backdrop" role="presentation" onClick={() => setShowOmRequiredPopup(false)}>
          <div className="m2-popup" role="dialog" aria-modal="true" aria-labelledby="m2-om-popup-title" onClick={(e) => e.stopPropagation()}>
            <div className="m2-popup-icon">OM</div>
            <h3 id="m2-om-popup-title">Upload OM Required</h3>
            <p>
              Please upload the Offering Memorandum before moving to the next module.
              This keeps underwriting and deal intake based on the submitted deal documents.
            </p>
            <div className="m2-popup-actions">
              <button className="m2-popup-secondary" onClick={() => setShowOmRequiredPopup(false)}>
                Stay Here
              </button>
              <button className="m2-popup-primary" onClick={() => {
                setShowOmRequiredPopup(false);
                forceNavigateToModule(3);
              }}>
                Upload OM
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Module2Page;
